import { useTranslation } from "react-i18next";
import useBehindTheScenes from "../hooks/useBehindTheScenes";
import useBtsPlayback from "../hooks/useBtsPlayback";
import { BtsTileSkeleton } from "../SeriesBtsSkeleton";
import BtsTile from "./BtsTile";
import SectionHeader from "./SectionHeader";
import SeriesRail from "./SeriesRail";
import type { SeriesDetailsShow } from "../hooks/useSeriesDetails";
import { Skeleton } from "@/components/ui/skeleton";

const RAIL_ITEMS = "gap-3 *:w-40 md:gap-4 md:*:w-44";

/**
 * Behind the Scenes (SHO-757): a rail of the series' BTS videos, View all to
 * the full list. Its skeleton while its query runs — the page's skeleton
 * draws the rail, so it must not vanish and come back — nothing when the
 * series has none.
 */
const BtsSection: React.FC<{
  show: SeriesDetailsShow;
  series: string;
  /** The page's paywall, as the episode strip's `onLockedClick`. */
  onLockedClick: () => void;
}> = ({ show, series, onLockedClick }) => {
  const { t } = useTranslation();
  const videos = useBehindTheScenes(show.id);
  const { onBtsClick, player } = useBtsPlayback(show, onLockedClick);

  if (videos === undefined) return <BtsSectionSkeleton />;
  if (videos.length === 0) return null;

  return (
    <section data-test-id="series-bts-section" className="flex flex-col gap-3">
      <SectionHeader
        title={t("series_details_bts_title_web")}
        viewAll={{
          to: "/series/$series/behind-the-scenes",
          series,
          testId: "series-bts-view-all",
        }}
      />
      <SeriesRail itemClassName={RAIL_ITEMS}>
        {videos.map((bts) => (
          <BtsTile key={bts.id} bts={bts} onClick={() => onBtsClick(bts)} />
        ))}
      </SeriesRail>
      {player}
    </section>
  );
};

/** The section's shape, also what the page's skeleton draws for it. */
const BtsSectionSkeleton = () => (
  <section className="flex flex-col gap-3">
    <div className="flex items-center justify-between">
      <Skeleton className="h-5.5 w-40" />
      <Skeleton className="h-4 w-16" />
    </div>
    <SeriesRail static itemClassName={RAIL_ITEMS}>
      {Array.from({ length: 5 }, (_, i) => (
        <BtsTileSkeleton key={i} />
      ))}
    </SeriesRail>
  </section>
);

export default Object.assign(BtsSection, { Skeleton: BtsSectionSkeleton });
